import {UntypedFormControl, UntypedFormGroup} from '@angular/forms'
import {Question} from 'app/models/questions/Question'
import {QuestionFactory} from 'app/models/questions/QuestionFactory'
import Query from 'devextreme/data/query'
import {IStructureElements} from 'app/interfaces/IStructureElements'
import {cloneDeep, isEqual, keys, parseInt} from 'lodash-es'
import {Matrix} from 'app/models/questions/Matrix'
import {SurveyStructureElementValidationError} from 'app/custom-errors/SurveyStructureElementValidationError'
import {IPage} from 'app/interfaces/IPage'
import {SurveyDefPropertyValidator} from 'app/models/survey-property-validator/SurveyDefPropertyValidator'
import {ISurveyDef} from 'app/interfaces/ISurveyDef'
import {SurveyPropertyValidator} from 'app/models/survey-property-validator/SurveyPropertyValidator'
import {IHaveRules, ISurvey} from 'app/interfaces/ISurvey'
import {
  SurveyStructureElementPropertyValidator
} from 'app/models/survey-property-validator/SurveyStructureElementPropertyValidator'
import {Header} from 'app/models/questions/Header'
import {ISurveyReward} from "../modules/landing-publication/publication-access.service"
import {ViewerTypeEnum} from 'app/enums/ViewerTypeEnum'
import {Image} from "./questions/Image";
import {UserService} from "../core/user/user.service";
import {IUser} from "../interfaces/IUser";
import {LangsListParamsService} from "@store/langs-list-params.service";
import {ImageUpload} from "./questions/ImageUpload";
import {AudioRecorder} from "./questions/AudioRecorder";

export interface ISurveyConstructorParams {
  singlePreviewMode: boolean,
  previewMode: boolean,
  applyRules: boolean,
  debugMode: boolean,
  aliasTitle?: Record<string, string>,
}

export const defaultSurveyConstructorParams: ISurveyConstructorParams = {
  singlePreviewMode: false,
  debugMode: false,
  previewMode: false,
  applyRules: true,
}

export class Survey implements ISurvey {
  private readonly _id: string
  private readonly _title: Record<string, string>
  private readonly _projectId: number
  private readonly _user: IUser
  private readonly _startTimeControlName = 'startTime'
  private readonly _endTimeControlName = 'endTime'
  private readonly _originalQuestions: Record<string, Question>
  private readonly _questions: Record<string, Question>
  private readonly _form: UntypedFormGroup
  private readonly _constructorParams: ISurveyConstructorParams = defaultSurveyConstructorParams;

  private _answers: Record<string, {}> = {}
  private _originalSurveyDef: ISurveyDef
  private _surveyDef: ISurveyDef
  private _pages: IPage[]
  private _reward: ISurveyReward = {
    hasReward: false,
    default: 0,
  }

  constructor(
    survey: ISurvey,
    private userService?: UserService,
    surveyConstructorParams: ISurveyConstructorParams = defaultSurveyConstructorParams,
  ) {

    new SurveyPropertyValidator(survey).validate()
    new SurveyDefPropertyValidator(survey.surveyDef).validate()
    new SurveyStructureElementPropertyValidator(survey.surveyDef.structureElements).validate()

    this.validateQuestionsIndexStructure(survey.surveyDef.structureElements, survey.surveyDef.questions)
    this.validateInfoTypesAreUnique(survey)

    this._id = survey.id
    this._title = surveyConstructorParams.aliasTitle || survey.surveyDef.title
    this._surveyDef = survey.surveyDef
    this._projectId = survey.projectId
    this._originalSurveyDef = survey.surveyDef
    this._user = userService.user$.value,
    this._reward = survey.reward
    this._questions = this.buildQuestionClasses()
    this._originalQuestions = this.buildQuestionClasses()
    this._form = this.buildForm()

    // this._singlePreviewMode = surveyConstructorParams.singlePreviewMode
    this._constructorParams = {
      ...defaultSurveyConstructorParams,
      ...surveyConstructorParams,
    }

    this.randomize()
    this.applyRules({})
  }

  validateForm(): boolean {
    let isValid = true
    this.pages.forEach((p, index) => {
      if (!this.validatePage(index)) {
        isValid = false
      }
    })
    return isValid
  }

  validatePage(pageIdx: number, currentPage = pageIdx): boolean {
    return this.pages[pageIdx].questions.every(q =>
      this.form.get(q.key).valid && pageIdx <= currentPage)
  }

  extractAnswers(): {} {
    const desiredFormKeys = this.removeHeaderKeysFromAnswers()
    let formAnswers = {}
    desiredFormKeys.forEach(k => {
      formAnswers[k] = this.form.value[k]
    })
    return formAnswers
  }

  findFirstInvalidQuestion() {
    for (const pageIdx in this.pages) {
      const page = this.pages[pageIdx]
      for (const idx in page.questions) {
        if (!this.form.get(page.questions[idx].key).valid) {
          return {
            question: page.questions[idx],
            pageIdx: parseInt(pageIdx)
          }
        }
      }
    }
  }

  findFirstInvalidQuestionBeforePage(maxPageIdx: number) {
    for (let pageIdx = 0; pageIdx < maxPageIdx && pageIdx < this.pages.length; pageIdx++) {
      const page = this.pages[pageIdx]
      for (const idx in page.questions) {
        if (!this.form.get(page.questions[idx].key).valid) {
          return {
            question: page.questions[idx],
            pageIdx: pageIdx
          }
        }
      }
    }
    return null
  }

  findFirstInvalidQuestionUpToPage(maxPageIdx: number) {
    for (let pageIdx = 0; pageIdx <= maxPageIdx && pageIdx < this.pages.length; pageIdx++) {
      const page = this.pages[pageIdx]
      for (const idx in page.questions) {
        if (!this.form.get(page.questions[idx].key).valid) {
          return {
            question: page.questions[idx],
            pageIdx: pageIdx
          }
        }
      }
    }
    return null
  }

  validatePagesBefore(currentPageIdx: number): boolean {
    for (let pageIdx = 0; pageIdx < currentPageIdx && pageIdx < this.pages.length; pageIdx++) {
      if (!this.validatePage(pageIdx)) {
        return false
      }
    }
    return true
  }

  validatePagesUpTo(currentPageIdx: number): boolean {
    for (let pageIdx = 0; pageIdx <= currentPageIdx && pageIdx < this.pages.length; pageIdx++) {
      if (!this.validatePage(pageIdx)) {
        return false
      }
    }
    return true
  }

  getQuestionsReferencedInStructureElements(): Question[] {
    let questions: Question[] = []

    if (this.surveyDef.structureElements.questionsGroups.questionsIndex) {
      const questionIndex = Object.values(this.surveyDef.structureElements.questionsGroups.questionsIndex).flatMap(el => el)
      questionIndex.forEach(qKey => {
        questions.push(this.questions[qKey])
      })
    }

    return questions
  }

  applyRules(answers: Record<string, {}>): void | false {

    // console.log('applyRules');

    if (this.singlePreviewMode) {
      console.log('this.singlePreviewMode***********')
      return false
    }

    if (Object.keys(answers).length) {
      this.answers = answers
    }

    this.resetStructureElements()

    const structureElements = this.surveyDef.structureElements
    const queryInput = this.mergeExtProfilesAndAnswers()

    if (this._constructorParams.applyRules) {

      // console.log('[survey.ts] Rules are EVALUATED');

      this.handlePageGroupRules(structureElements, queryInput)
      this.handlePageRules(structureElements, queryInput)
      this.handleQuestionGroupRules(structureElements, queryInput)
      this.handleQuestionRules(structureElements, queryInput)
      this.handleStatementRules(structureElements, queryInput)
      this.handleRowRules(structureElements, queryInput)

    } else {

      // console.log('[survey.ts] Rules are bypassed');

    }

    this.pages = this.buildPages()
  }

  private removeHeaderKeysFromAnswers() {
    const headerKeys = this.getQuestionsReferencedInStructureElements().filter(q => q instanceof Header).map(q => q.key)
    return Object.keys(this.form.value).filter(q => !headerKeys.includes(q))
  }

  private shuffleKeys(keys: string[], positionLockedKeys: string[]) {
    const originalIndex = [...keys]

    for (let i = keys.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [keys[i], keys[j]] = [keys[j], keys[i]]
    }

    positionLockedKeys.forEach(lockedKey => {
      const correctKeyPosition = originalIndex.findIndex(el => el === lockedKey)
      const actualKeyPosition = keys.findIndex(el => el === lockedKey)
      if (actualKeyPosition !== correctKeyPosition) {
        const valueToMove = keys[actualKeyPosition]
        keys[actualKeyPosition] = keys[correctKeyPosition]
        keys[correctKeyPosition] = valueToMove
      }
    })
  }

  private validateInfoTypesAreUnique(survey: ISurvey) {
    const questions = survey.surveyDef.questions
    const questionsIndex = survey.surveyDef.structureElements.questionsGroups.questionsIndex

    if (!questionsIndex) {
      return
    }

    const infoTypeQuestionSet = new Set()

    const infoTypes = [
      ViewerTypeEnum.ADDRESS,
      ViewerTypeEnum.PHONE,
      ViewerTypeEnum.NAME,
      ViewerTypeEnum.EMAIL
    ]

    Object.keys(questionsIndex).forEach(qgKey => {
      const questionsInGroup = questionsIndex[qgKey]
      for (const questionsInGroupKey in questionsInGroup) {
        const questionKey = questionsInGroup[questionsInGroupKey]
        const question = questions[questionKey]

        if (infoTypes.includes(question.viewerType)) {
          if (infoTypeQuestionSet.has(question.viewerType)) {
            throw new SurveyStructureElementValidationError().duplicatedInfoTypeQuestions(survey, question.viewerType)
          }
          infoTypeQuestionSet.add(question.viewerType)
        }
      }
    })
  }

  private validateQuestionsIndexStructure(structureElements: IStructureElements, questions) {
    const questionsIndex = structureElements.questionsGroups.questionsIndex

    if (!questionsIndex) {
      return
    }

    Object.keys(questionsIndex).forEach(qgKey => {
      const questionsInGroup = questionsIndex[qgKey]
      for (const questionsInGroupKey in questionsInGroup) {
        const questionKey = questionsInGroup[questionsInGroupKey]
        if (!questions[questionKey]) {
          throw new SurveyStructureElementValidationError().missingQuestionFromQuestionsIndex(questionKey, Object.keys(questions))
        }
      }
    })
  }

  private buildForm(): UntypedFormGroup {
    let form = new UntypedFormGroup({})
    this.getQuestionsReferencedInStructureElements().forEach(question => {

      form.addControl(
        question.key,
        question.buildQuestionFormGroup(),
      )

    })

    this.setupElapsedTimeControls(form)

    return form
  }

  private setupElapsedTimeControls(form: UntypedFormGroup) {
    form.addControl(this.startTimeControlName, new UntypedFormControl(null))
    form.addControl(this.endTimeControlName, new UntypedFormControl(null))
  }

  private buildPages(): IPage[] {

    let pages: IPage[] = []
    let pageQuestions: Question[] = []
    let structureElements = this.surveyDef.structureElements

    structureElements.pagesGroupsIndex.forEach(pgKey => {
      const pageKeys = structureElements.pagesGroups.pagesIndex[pgKey]

      if (pageKeys) {
        pageKeys.forEach(pageKey => {

          const groupOfQuestionsInPage = structureElements.pages.questionsGroupsIndex[pageKey]

          groupOfQuestionsInPage.forEach(qgKey => {
            const questionsIndex = structureElements.questionsGroups.questionsIndex
            if (questionsIndex && questionsIndex[qgKey]) {
              questionsIndex[qgKey].forEach(qKey => {
                pageQuestions.push(this.questions[qKey])
              })
            }
          })

          pages.push({uuid: pageKey, questions: pageQuestions})

          pageQuestions = []
        })
      }
    })

    return pages
  }

  // private formatInfoQuestionGroup

  private resetStructureElements() {
    this.surveyDef = cloneDeep(this.originalSurveyDef)
  }

  private mergeExtProfilesAndAnswers(): Record<string, {}> {

    let queryInput = cloneDeep(this.answers)

    if (Object.keys(this.user).length && this.userService.user$?.value?.extProfiles) {
      Object.keys(this.userService.user$.value.extProfiles).forEach(k => {
        if (!queryInput[k]) {
          queryInput[k] = this.userService.user$.value.extProfiles[k]
        }
      })
    }

    return queryInput
  }

  private handleStatementRules(structureElements: IStructureElements, queryInput: Record<string, {}>): void {
    if (!structureElements.questionsGroups.questionsIndex) {
      return
    }

    const questions = this.findQuestionsReferencedInStructureElements(structureElements)

    for (const questionIdx in questions) {

      const questionKey = questions[questionIdx].key
      const question = this.originalQuestions[questionKey]
      if (!question.statementsRulesIndex) {
        continue
      }

      if (question.statements && Object.keys(question.statements).length) {

        let excludedStatements = []
        question.statementsRulesIndex.forEach(sKey => {
          const statementRules = this.originalQuestions[questionKey].statements[sKey]?.rules

          if (statementRules) {

            if (statementRules.visibleIf?.finalExpression) {
              const statementVisibleIfRules = statementRules.visibleIf.finalExpression

              Query([queryInput])
                .filter(statementVisibleIfRules)
                .count()
                .then((r) => {
                  if (r === 0) {
                    excludedStatements.push(sKey)
                  }
                })
            }

            if (statementRules.hideIf?.finalExpression) {
              const statementHideIfRules = statementRules.hideIf.finalExpression

              Query([queryInput])
                .filter(statementHideIfRules)
                .count()
                .then((r) => {
                  if (r === 1) {
                    excludedStatements.push(sKey)
                  }
                })
            }
          }
        })

        const filteredStatements = Object.keys(this.originalQuestions[questionKey].statements).map(s =>
          question.statements[s]).filter(s => !excludedStatements.includes(s.key))

        const questionClone = cloneDeep(question)

        questionClone.statements = {}

        filteredStatements.forEach(statement => {
          questionClone.statements[statement.key] = statement
        })

        if (question instanceof Matrix) {
          excludedStatements.forEach(sKey => {
            question.rowKeys.forEach(rKey => {
              const selectedDefaultValue = question.buildQuestionFormGroup().value[rKey][question.formControlName][sKey]
              this.form.controls[question.key].get(rKey).get(question.formControlName).get(sKey).setValue(selectedDefaultValue, {emitEvent: false})
              this.form.controls[question.key].get(rKey).get(question.formControlName).get(sKey).disable({emitEvent: false})
              if (question instanceof ImageUpload || question instanceof AudioRecorder){
                this.form.controls[question.key].get(rKey).get(question.imageControlName).get(sKey).setValue('', {emitEvent: false})
              }
            })
          })
        }

        if (!isEqual(this.questions[question.key].statements, questionClone.statements)) {

          if (question instanceof Matrix) {
            questionClone.statementKeys.forEach(sKey => {
              if (!excludedStatements.includes(sKey)) {
                question.rowKeys.forEach(rKey => {
                  this.form.controls[question.key].get(rKey).get(question.formControlName).get(sKey).enable({emitEvent: false})

                  const selectedDefaultValue = question.buildQuestionFormGroup().value[rKey][question.formControlName][sKey]

                  this.form.controls[question.key].get(rKey).get(question.formControlName).get(sKey).reset(selectedDefaultValue, {emitEvent: false})
                  if (question instanceof ImageUpload || question instanceof AudioRecorder) {
                    this.form.controls[question.key].get(rKey).get(question.imageControlName).get(sKey).setValue('', {emitEvent: false})
                  }
                  if (this.form.controls[question.key].get(rKey).get(question.commentsControlName).get(sKey)) {
                    const commentDefaultValue = question.buildQuestionFormGroup().value[rKey][question.commentsControlName][sKey]
                    this.form.controls[question.key].get(rKey).get(question.commentsControlName).get(sKey).reset(commentDefaultValue, {emitEvent: false})
                  }
                })
              }
            })
          }

          questionClone.statementsOrder = questionClone.statementsOrder.filter(sKey => !excludedStatements.includes(sKey))

          this.questions[question.key] = questionClone
        }
      }
    }
  }

  private handleRowRules(structureElements: IStructureElements, queryInput: Record<string, {}>) {
    if (!structureElements.questionsGroups.questionsIndex) {
      return
    }

    const questions = this.findQuestionsReferencedInStructureElements(structureElements)

    for (const questionIdx in questions) {
      const questionKey = questions[questionIdx].key

      const question = this.originalQuestions[questionKey] as Matrix

      if (!question.rowsRulesIndex) {
        continue
      }

      const matrix = this.originalQuestions[questionKey] as Matrix

      let excludedRows = []

      matrix.rowsRulesIndex.forEach(rKey => {
        const rowRules = matrix.rows[rKey]?.rules
        if (rowRules) {

          if (rowRules.visibleIf?.finalExpression) {
            const rowVisibleIfRules = rowRules.visibleIf.finalExpression

            Query([queryInput])
              .filter(rowVisibleIfRules)
              .count()
              .then((r) => {
                if (r === 0) {
                  excludedRows.push(rKey)
                }
              })
          }

          if (rowRules.hideIf?.finalExpression) {
            const rowHideIfRules = rowRules.hideIf.finalExpression

            Query([queryInput])
              .filter(rowHideIfRules)
              .count()
              .then((r) => {
                if (r === 1) {
                  excludedRows.push(rKey)
                }
              })
          }
        }
      })

      const filteredRows = Object.keys(matrix.rows).map(r => matrix.rows[r]).filter(r => !excludedRows.includes(r.key))

      const matrixClone = cloneDeep(matrix)

      matrixClone.rows = {}
      matrixClone.statements = this.questions[matrix.key].statements
      matrixClone.statementsOrder = this.questions[matrix.key].statementsOrder

      filteredRows.forEach(row => {
        matrixClone.rows[row.key] = row
      })

      const actualMatrix = this.questions[matrix.key] as Matrix

      excludedRows.forEach(rKey => {
        matrixClone.statementKeys.forEach(sKey => {
          this.form.controls[matrixClone.key].get(rKey).get(matrixClone.formControlName).get(sKey).setValue(false, {emitEvent: false})
          this.form.controls[matrixClone.key].get(rKey).get(matrixClone.formControlName).get(sKey).disable({emitEvent: false})
        })
        this.form.controls[matrixClone.key].get(rKey).disable({emitEvent: false})
      })

      if (!isEqual(actualMatrix.rowKeys, matrixClone.rowKeys)) {

        matrixClone.rowKeys.forEach(rKey => {
          if (!excludedRows.includes(rKey)) {
            if (this.form.controls[matrixClone.key].get(rKey).disabled) {
              this.form.controls[matrixClone.key].get(rKey).enable({emitEvent: false})
              this.form.controls[matrixClone.key].get(rKey).reset(question.buildQuestionFormGroup().value[rKey], {emitEvent: false})
            }
          }
        })

        matrixClone.rowsOrder = matrixClone.rowsOrder.filter(rKey => !excludedRows.includes(rKey))

        this.questions[matrix.key] = matrixClone
      }

    }
  }

  private handleQuestionRules(structureElements: IStructureElements, queryInput: Record<string, {}>) {
    const questionGroups = structureElements.questionsGroups
    if (!Object.keys(this.questions).length ||
      !questionGroups ||
      !questionGroups.questionsIndex
    ) {
      return
    }

    // Fonction pour convertir les objets (stockés par Firebase) en tableaux pour Query
    const convertObjectsToArrays = (obj: any): any => {
      if (obj === null || obj === undefined) {
        return obj;
      }
      
      if (Array.isArray(obj)) {
        return obj.map(item => convertObjectsToArrays(item));
      }
      
      if (typeof obj === 'object') {
        const keys = Object.keys(obj);
        const allNumericKeys = keys.length > 0 && keys.every(key => /^\d+$/.test(key));
        
        if (allNumericKeys) {
          const sortedKeys = keys.map(k => parseInt(k, 10)).sort((a, b) => a - b);
          return sortedKeys.map(key => convertObjectsToArrays(obj[key.toString()]));
        }
        
        const converted: any = {};
        for (const [key, value] of Object.entries(obj)) {
          converted[key] = convertObjectsToArrays(value);
        }
        return converted;
      }
      
      return obj;
    };

    let excludedQuestions = []

    Object.keys(questionGroups.questionsIndex).forEach(qgKey => {
      const questionsInGroup = questionGroups.questionsIndex[qgKey]

      for (const questionInGroupIdx in questionsInGroup) {
        const qKey = questionsInGroup[questionInGroupIdx]
        const questionRules = this.questions[qKey]?.rules

        if (questionRules?.hideIf?.finalExpression) {
          // Convertir d'objet en tableau si nécessaire (pour les règles stockées dans Firebase)
          let hideIfRuleForThisQuestion = convertObjectsToArrays(questionRules.hideIf.finalExpression)

          // Évaluer la règle de manière synchrone
          const hideIfResult: any[] = Query([queryInput])
            .filter(hideIfRuleForThisQuestion)
            .toArray() as any[];

          // Si hideIf retourne un résultat, la question doit être masquée
          if (hideIfResult && hideIfResult.length > 0) {
            excludedQuestions.push(qKey)
          }
        }

        if (questionRules?.visibleIf?.finalExpression) {
          // Convertir d'objet en tableau si nécessaire (pour les règles stockées dans Firebase)
          let visibleIfRuleForThisQuestion = convertObjectsToArrays(questionRules.visibleIf.finalExpression)

          // Évaluer la règle de manière synchrone
          const visibleIfResult: any[] = Query([queryInput])
            .filter(visibleIfRuleForThisQuestion)
            .toArray() as any[];

          // Si visibleIf ne retourne pas de résultat, la question doit être masquée
          if (!visibleIfResult || visibleIfResult.length === 0) {
            excludedQuestions.push(qKey)
          }
        }

      }
      this.enableAllQuestionControls(structureElements)
      this.disableControlsOfExcludedQuestions(excludedQuestions)

      questionGroups.questionsIndex[qgKey] = questionGroups.questionsIndex[qgKey].filter(idx => !excludedQuestions.includes(idx))
    })
  }

  private disableControlsOfExcludedQuestions(excludedQuestions: string[]): void {
    excludedQuestions.forEach(eQ => {
      this.form.controls[eQ].disable({emitEvent: false})
    })
  }

  private enableAllQuestionControls(structureElements: IStructureElements): void {
    const questions = this.findQuestionsReferencedInStructureElements(structureElements)

    questions.forEach((question) => {
      if (this.form.controls[question.key].disabled) {
        this.form.controls[question.key].enable({emitEvent: false})
        this.form.controls[question.key].reset(question.buildQuestionFormGroup().value, {emitEvent: false})
      }
    })
  }

  private findQuestionsReferencedInStructureElements(structureElements: IStructureElements) {
    const questionKeys = Object.values(structureElements.questionsGroups.questionsIndex).flatMap(el => el)
    return questionKeys.map(qKey => this.questions[qKey])
  }

  private handleQuestionGroupRules(structureElements: IStructureElements, queryInput: Record<string, {}>) {
    if (!structureElements.questionsGroups.properties) {
      return
    }

    let excludedQuestionGroups = []

    Object.keys(structureElements.pages.questionsGroupsIndex).forEach(pKey => {
      const questionGroupsInPage = structureElements.pages.questionsGroupsIndex[pKey]

      questionGroupsInPage.forEach(qgKey => {
        const questionGroupRules = structureElements.questionsGroups.properties[qgKey]?.rules
        const properties = structureElements.questionsGroups.properties[qgKey]
        if (properties.subType === 'infos' && properties?.infoSpecial?.onlyAnonymous && !this._user.isPublic) {
          excludedQuestionGroups.push(qgKey)
        }
        if (questionGroupRules?.hideIf?.finalExpression) {
          const hideIfRuleForThisQuestionGroup = questionGroupRules.hideIf.finalExpression

          Query([queryInput])
            .filter(hideIfRuleForThisQuestionGroup)
            .count()
            .then((r) => {
              if (r === 1) {
                excludedQuestionGroups.push(qgKey)
              }
            })
        }

        if (questionGroupRules?.visibleIf?.finalExpression) {
          const visibleIfRuleForThisQuestionGroup = questionGroupRules.visibleIf.finalExpression

          Query([queryInput])
            .filter(visibleIfRuleForThisQuestionGroup)
            .count()
            .then((r) => {
              if (r === 0) {
                excludedQuestionGroups.push(qgKey)
              }
            })
        }
      })

      structureElements.pages.questionsGroupsIndex[pKey] = structureElements.pages.questionsGroupsIndex[pKey].filter(idx => !excludedQuestionGroups.includes(idx))
    })

  }

  private handlePageRules(structureElements: IStructureElements, queryInput: Record<string, {}>) {
    if (!structureElements.pages.properties) {
      return
    }

    let excludedPages = []

    structureElements.pagesGroupsIndex.forEach(gKey => {
      const pagesFromGroup = structureElements.pagesGroups.pagesIndex[gKey]
      pagesFromGroup.forEach(pkey => {
        const pageRules = structureElements.pages.properties[pkey]?.rules

        if (pageRules?.hideIf?.finalExpression) {
          const hideIfRuleForThisPage = pageRules.hideIf.finalExpression

          Query([queryInput])
            .filter(hideIfRuleForThisPage)
            .count()
            .then((r) => {
              if (r === 1) {
                excludedPages.push(pkey)
              }
            })
        }

        if (pageRules?.visibleIf?.finalExpression) {
          const visibleIfRuleForThisPage = pageRules.visibleIf.finalExpression

          Query([queryInput])
            .filter(visibleIfRuleForThisPage)
            .count()
            .then((r) => {
              if (r === 0) {
                excludedPages.push(pkey)
              }
            })
        }

      })

      structureElements.pagesGroups.pagesIndex[gKey] = structureElements.pagesGroups.pagesIndex[gKey].filter(idx => !excludedPages.includes(idx))
    })
  }

  private handlePageGroupRules(structureElements: IStructureElements, queryInput: Record<string, {}>) {
    if (!structureElements.pagesGroups.properties) {
      return
    }

    const pageGroupKeysWithRules = Object.keys(structureElements.pagesGroups.properties)
    let excludedPageGroupIndexes = []

    pageGroupKeysWithRules.forEach(key => {

      const groupRule = structureElements.pagesGroups.properties[key]

      if (groupRule.rules?.hideIf?.finalExpression) {
        const hideIfRuleForThisPageGroup = groupRule.rules.hideIf.finalExpression

        Query([queryInput])
          .filter(hideIfRuleForThisPageGroup)
          .count()
          .then((r) => {
            if (r === 1) {
              excludedPageGroupIndexes.push(key)
            }
          })
      }

      if (groupRule.rules?.visibleIf?.finalExpression) {
        const visibleIfRuleForThisPageGroup = groupRule.rules.visibleIf.finalExpression

        Query([queryInput])
          .filter(visibleIfRuleForThisPageGroup)
          .count()
          .then((r) => {
            if (r === 0) {
              excludedPageGroupIndexes.push(key)
            }
          })
      }
    })

    structureElements.pagesGroupsIndex = structureElements.pagesGroupsIndex.filter(idx => !excludedPageGroupIndexes.includes(idx))
  }

  private buildQuestionClasses(): Record<string, Question> {
    let questions: Record<string, Question> = {}
    Object.keys(this.surveyDef.questions)
      .forEach(key => {
        questions[key] = QuestionFactory.buildQuestionClass(this.surveyDef.questions[key])
      })
    return questions
  }

  private randomize(): void | false {
    if (this.singlePreviewMode) {
      return false
    }

    this.surveyDef.structureElements.pagesGroupsIndex.forEach(pgKey => {
      const pagesGroups = this.surveyDef.structureElements.pagesGroups
      const pageGroupProperties = pagesGroups.properties
      const pagesIndex = pagesGroups.pagesIndex[pgKey]

      if (!pagesIndex) {
        return
      }

      const pages = this.surveyDef.structureElements.pages
      if (
        pageGroupProperties &&
        pageGroupProperties[pgKey] &&
        pageGroupProperties[pgKey].randomize
      ) {
        const pageLockedKeys = []
        pagesIndex.forEach(pKey => {
          if (pages.properties &&
            pages.properties[pKey] &&
            pages.properties[pKey].positionLocked
          ) {
            pageLockedKeys.push(pKey)
          }
        })
        this.shuffleKeys(pagesIndex, pageLockedKeys)
      }

      pagesIndex.forEach(pKey => {
        const pageProperties = pages.properties
        const questionGroupIndex = pages.questionsGroupsIndex[pKey]

        const questionGroups = this.surveyDef.structureElements.questionsGroups

        if (
          pageProperties &&
          pageProperties[pKey] &&
          pageProperties[pKey].randomize
        ) {
          const questionGroupLockedKeys = []
          questionGroupIndex.forEach(qgKey => {
            if (questionGroups.properties &&
              questionGroups.properties[qgKey] &&
              questionGroups.properties[qgKey].positionLocked
            ) {
              questionGroupLockedKeys.push(qgKey)
            }
          })
          this.shuffleKeys(questionGroupIndex, questionGroupLockedKeys)
        }

        questionGroupIndex.forEach(qgKey => {
          const questionProperties = questionGroups.properties
          const questionsIndex = questionGroups.questionsIndex
          if (questionsIndex) {
            const questionKeys = questionsIndex[qgKey]

            if (
              questionProperties &&
              questionProperties[qgKey] &&
              questionProperties[qgKey].randomize
            ) {
              const questionPositionLockedKeys = []
              this.getQuestionsReferencedInStructureElements().forEach(q => {
                if (q.positionLocked) {
                  questionPositionLockedKeys.push(q.key)
                }
              })
              this.shuffleKeys(questionKeys, questionPositionLockedKeys)
            }
          }
        })
      })
    })

    this.shuffleQuestionElements()
    this.originalSurveyDef = cloneDeep(this.surveyDef)
  }

  private shuffleQuestionElements() {
    if (Object.keys(this.questions).length) {
      this.shuffleStatements()
      this.shuffleRows()
    }
  }

  private shuffleStatements() {
    for (const questionIdx in this.questions) {

      const question = this.originalQuestions[questionIdx]

      if (!question.randomizeStatements || !question.statementsOrder) {
        continue
      }

      const statementPositionLockedKeys = []
      Object.keys(question.statements).forEach(sKey => {
        if (question.statements[sKey].positionLocked) {
          statementPositionLockedKeys.push(sKey)
        }
      })

      this.shuffleKeys(question.statementsOrder, statementPositionLockedKeys)

      this.questions[question.key] = cloneDeep(question)
    }
  }

  private shuffleRows() {
    for (const questionsKey in this.questions) {

      const question = this.originalQuestions[questionsKey]

      if (
        !(question instanceof Matrix) ||
        !question.randomizeRows ||
        !question.rowsOrder ||
        (question instanceof Image)
      ) {
        continue
      }

      const matrix = this.questions[question.key] as Matrix

      const rowPositionLockedKeys = []
      Object.keys(matrix.rows).forEach(rKey => {
        if (matrix.rows[rKey].positionLocked) {
          rowPositionLockedKeys.push(rKey)
        }
      })

      this.shuffleKeys(matrix.rowsOrder, rowPositionLockedKeys)

      this.questions[question.key] = cloneDeep(matrix)
    }
  }

  get originalSurveyDef() {
    return this._originalSurveyDef
  }

  get answers() {
    return this._answers
  }

  get user() {
    return this._user
  }

  get form(): UntypedFormGroup {
    return this._form
  }

  set surveyDef(surveyDef: ISurveyDef) {
    this._surveyDef = surveyDef
  }

  set answers(answers) {
    this._answers = answers
  }

  set pages(pages) {
    this._pages = pages
  }

  get id() {
    return this._id
  }

  get projectId() {
    return this._projectId
  }

  get questions() {
    return this._questions
  }

  get title() {
    return this._title;
  }

  get surveyDef() {
    return this._surveyDef
  }

  get pages(): IPage[] {
    return this._pages
  }

  get startTimeControlName() {
    return this._startTimeControlName
  }

  get endTimeControlName() {
    return this._endTimeControlName
  }

  get originalQuestions() {
    return this._originalQuestions
  }

  set originalSurveyDef(surveyDef) {
    this._originalSurveyDef = surveyDef
  }

  get singlePreviewMode() {
    return this._constructorParams.singlePreviewMode
  }


  set reward(reward: ISurveyReward) {
    this._reward = reward
  }

  get reward() {
    return this._reward
  }

  haveRules: IHaveRules;
}
